/**
 * InvoiceCancelDialog — the ONE cancellation confirmation + API call for
 * invoices (Slice 2D).
 *
 * Until Slice 2C the "Storniert" button wrote the status string through the
 * generic endpoint: no eligibility check, no timestamp, no reason, no audit
 * entry, and no objection to cancelling an invoice that had already been paid.
 * That door is now shut server-side, and this dialog is what replaced it.
 *
 * Mirrors WorkReportCancelDialog deliberately — one cancellation gesture in this
 * product should not have two different shapes. The reason gates the confirm
 * button (3..500 chars after trim, the same bounds the backend enforces), and
 * `pending` is part of that gate so a double click cannot send two cancels.
 *
 * WHAT THIS DIALOG DOES NOT DO: decide whether the invoice may be cancelled.
 * `paid`, a stray `paid_at`, an already-cancelled row — all of that is answered
 * by the server with a 409 and a stable code. Re-deriving any of it from the
 * fields on screen would be a second cancelability matrix that can disagree with
 * the first, and the disagreement would always be resolved in favour of the one
 * that cannot see the database.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { cancelAdminInvoice } from "../../../api/adminData";

export const CANCEL_REASON_MIN = 3;
export const CANCEL_REASON_MAX = 500;

export default function InvoiceCancelDialog({
  invoice, // the invoice pending cancellation, or null when closed
  onClose,
  onCancelled,
}) {
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);

  // A fresh target starts with a fresh reason — a text left over from the
  // previously cancelled invoice must never be submitted for the next one.
  useEffect(() => {
    setReason("");
  }, [invoice?.id]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < CANCEL_REASON_MIN;
  const tooLong = trimmed.length > CANCEL_REASON_MAX;
  const canSubmit = Boolean(invoice) && !pending && !tooShort && !tooLong;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setPending(true);
    try {
      const updated = await cancelAdminInvoice(invoice.id, trimmed);
      toast.success("Rechnung storniert.");
      onCancelled?.(updated);
    } catch (err) {
      // The server's sentence, whatever the code was. The dialog stays open and
      // usable so a refusal is visibly a refusal and not a silent success.
      toast.error(err?.message || "Rechnung konnte nicht storniert werden.");
    } finally {
      setPending(false);
    }
  };

  const label = invoice?.invoice_number || (invoice ? `#${invoice.id}` : "");

  return (
    <AlertDialog
      open={Boolean(invoice)}
      onOpenChange={(open) => {
        if (!open && !pending) onClose?.();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Rechnung stornieren?</AlertDialogTitle>
          <AlertDialogDescription>
            {invoice
              ? `„${label}" wird als storniert markiert. Bereits bezahlte ` +
                "Rechnungen können auf diesem Weg nicht storniert werden. " +
                "Diese Aktion kann nicht rückgängig gemacht werden."
              : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-1.5">
          <label
            htmlFor="invoice-cancel-reason"
            className="text-[12px] font-medium text-muted-foreground"
          >
            Stornierungsgrund (erforderlich)
          </label>
          <textarea
            id="invoice-cancel-reason"
            rows={3}
            maxLength={CANCEL_REASON_MAX + 1}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={pending}
            placeholder="z. B. Mietverhältnis vor Einzug storniert."
            className="w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          />
          {tooShort ? (
            <p className="m-0 text-[11px] text-muted-foreground">
              Mindestens {CANCEL_REASON_MIN} Zeichen.
            </p>
          ) : null}
          {tooLong ? (
            <p className="m-0 text-[11px] text-muted-foreground">
              Höchstens {CANCEL_REASON_MAX} Zeichen.
            </p>
          ) : null}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
          {/* Not AlertDialogAction: that primitive closes on click even when the
              submit must be blocked, and it would close before the API answers —
              a 409 would then look like a successful cancellation. */}
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleConfirm}
            className="inline-flex h-10 items-center justify-center rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:pointer-events-none disabled:opacity-50"
          >
            {pending ? "Storniert …" : "Rechnung stornieren"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
